/**
 * Created by Ella Ma on 04/17/2019.
 * Description:
 *
 * ------ maintenance history ------
 * 14/04/2022 By Marcus Zhao add filter tab for map tile setting.
 *       use mapFilter as a new parameter in tile setting json
 * Marcus Zhao 07/07/2022   clear mapconfig data :tileFilterConfig.mapConfig = [];
 */

import { Component, OnInit, OnDestroy, Input, ViewChild } from '@angular/core';
import { Subscription, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TileChooseConfig, TileChooseEventType, Validatable } from './tile-choose-visualization/tile-choose-visualization.model';
import { WIZARD_MENU, TileModel, TileFilter, GridVisualizationSettings } from '../../../tamalelibs/models/tile.model';
import { WizardMenuConfig, Status, WizardMenuEventType, ValidStatus } from '../../../widgets/wizard-menu/wizard-menu.config';
import { TileFilterService } from '../../../tamalelibs/services/tile-filter.service';
import { TileFilterConfig, TileFilterEventType } from './tile-filter/tile-filter.model';
import * as _ from 'lodash';
import { TileSettingsConfig } from './tile-settings/tile-settings.model';
import { TileSettingsComponent } from './tile-settings/tile-settings.component';
import { BriefConfig } from '../../../widgets/widgets.model';
import { TileSettingEvents, TileOperations } from './tile-setting.model';
import { TileColumnSettingComponent } from './tile-column-setting/tile-column-setting.component';
import { TamDashboardTileType, TamDashboardEvents, TamTileSettingsEvents } from '../../tam-dashboard/tam-dashboard.model';
import { DataEventType } from './data-visualizations/data-visualizations.model';
import { AlertWindowService } from '../../../widgets/alert-window/alert-window.service';
import { TileSettingService } from '../../../services/tile-setting.service';
import { defaultSource, defaultSubmitter, defaultEntryClass, TileColumn } from '../../../tamalelibs/models/tile-column.model';
import { NoteTileSettingsService } from './tile-chart-setting/note-tile-chart-setting/note-tile-chart-setting.service';
import { ScreenFocusType } from '../../../models/saved-screen';

@Component({
    selector: 'tile-setting-wrapper',
    templateUrl: './tile-setting-wrapper.component.html',
    styleUrls: ['./tile-setting-wrapper.component.scss']
})
export class TileSettingWrapperComponent implements OnInit, OnDestroy {

    @Input() config: BriefConfig;
    @ViewChild('tileSettingsComp', { static: false }) tileSettingsComp: TileSettingsComponent;
    @ViewChild('tileColumnSettingsComp', { static: false }) tileColumnSettingsComp: TileColumnSettingComponent;
    @ViewChild('tileChartComp', { static: false }) tileChartComp: Validatable;

    addVisualizationsConfig: BriefConfig = new BriefConfig();
    addVisualizationType: number;
    currentTileId: string;
    previewable = true;
    showNextBtn = true;
    showPreviousBtn = false;
    tileChooseConfig: TileChooseConfig = new TileChooseConfig();
    tileFilterConfig: TileFilterConfig = new TileFilterConfig();
    tileSettings: TileSettingsConfig = new TileSettingsConfig();
    wizardConfig: WizardMenuConfig = new WizardMenuConfig();
    tileConfig: TileModel = new TileModel();
    visualizationType = TamDashboardTileType;
    updateGridSettings$ = new Subject();
    tileColumnsChange$ = new Subject();

    private _tileOperate = '';
    private _destroySubscriptions: Subscription[] = [];
    private _defaultVisualizationSettingsStatus;
    private _defaultTileConfig: TileModel = new TileModel();
    private _screenFocusType = 0;

    constructor(
        private _alertWindow: AlertWindowService,
        private _tileFilterService: TileFilterService,
    ) { }

    ngOnInit() {
        this.initWizardData();
        this.initTileConfigData();
        this.subscriptions();
    }

    ngOnDestroy(): void {
        this._destroySubscriptions.forEach(item => item.unsubscribe());
    }

    initWizardData() {
        const isNewTile = !this.tileConfig.tileName;
        const status = (this._tileOperate === TileOperations.ADD || isNewTile) ? Status.disabled : Status.default;
        this.wizardConfig.config.menuData = [{
            icon: 'dashboard',
            title: 'Choose Visualization',
        }, {
            icon: 'filter-o',
            title: 'Filter Your Data'
        }, {
            icon: 'config-data-source',
            title: 'Configure Your Data Source',
            status: (!this._tileOperate && isNewTile) ? Status.disabled : Status.default
        }, {
            icon: 'visualization',
            title: 'Visualization Settings',
            status: status
        }, {
            icon: 'detail-setting',
            title: 'Tile Settings',
            status: status
        }];
        if (this._tileOperate === TileOperations.ADD) {
            this.wizardConfig.config.active = 1;
        } else if (this._tileOperate === TileOperations.COPY) {
            this.wizardConfig.config.active = this.wizardConfig.config.menuData.length - 1;
        } else {
            this.wizardConfig.config.active = 0;
        }
        this._defaultVisualizationSettingsStatus = this.wizardConfig.config.menuData[WIZARD_MENU.CONFIGURE].status;
        if (this.tileConfig.visualization.type === TamDashboardTileType.METADATA) {
            this.wizardConfig.config.menuData[WIZARD_MENU.VISUALIZATION].status = Status.notSet;
        }
        if (this.tileConfig.visualization.type === TamDashboardTileType.EDITOR) {
            this.wizardConfig.config.menuData[1]['status'] = Status.disabled;
            this.wizardConfig.config.menuData[2]['status'] = Status.disabled;
            this.wizardConfig.config.menuData[3]['status'] = Status.disabled;
            this.wizardConfig.config.menuData[4]['status'] = Status.selected;
            if (this._tileOperate === TileOperations.ADD || this._tileOperate === TileOperations.COPY) {
                this.wizardConfig.config.active = this.wizardConfig.config.menuData.length - 1;
            } else {
                this.wizardConfig.config.active = 0;

            }
        }
        if (this.tileConfig.visualization.type === TamDashboardTileType.MAP) {
            this.wizardConfig.config.menuData[0]['status'] = Status.selected;
            this.wizardConfig.config.menuData[2]['status'] = Status.disabled;
            this.wizardConfig.config.menuData[3]['status'] = Status.disabled;
            this.wizardConfig.config.menuData[4]['status'] = Status.default;
            this.wizardConfig.config.active = 0;
        }
        if (this.tileConfig.visualization.type === TamDashboardTileType.NOTES) {
            this._prepareNoteTileDefaults();
        }
        if (this.tileConfig && this.tileConfig.visualization) {
            this.wizardConfig.config.menuData.forEach((item, index) => {
                if (this._tileOperate === TileOperations.ADD && index === 0) {
                    item.validStatus = ValidStatus.validPass;
                } else if (!this._tileOperate || this._tileOperate === TileOperations.COPY) {
                    item.validStatus = (isNewTile || item.status === Status.notSet) ? ValidStatus.default : ValidStatus.validPass;
                }
            });
        }
    }

    initTileConfigData() {
        this.initFooterBtn();
        this.initVisualData();
        this.initTileFilterData();
        this.initTileSettingsData();
    }

    initFooterBtn() {
        if (this.tileConfig.visualization.type === TamDashboardTileType.EDITOR) {
            this.showNextBtn = (this.wizardConfig.config.active === 0);
            this.showPreviousBtn = (this.wizardConfig.config.active === this.wizardConfig.config.menuData.length - 1);
            this.previewable = false;
        } else {
            this.showNextBtn = true;
            this.showPreviousBtn = false;
            this.previewable = true;
        }
    }

    initVisualData() {
        this.tileChooseConfig.config = this.tileConfig.visualization;
    }

    initTileFilterData() {
        let _filtersConfig;
        this.tileFilterConfig.config = [];
        this.tileFilterConfig.mapConfig = null;
        const initEntityAllValue = () => {
            _filtersConfig = this._tileFilterService.getNewFilterDefaultInstance('Entity Type', 'EntityType', false, true, true);
            _filtersConfig.value = 'all';
            this.tileFilterConfig.config.push(_filtersConfig);
        };
        const initNodeAllValue = () => {
            _filtersConfig = this._tileFilterService.getNewFilterDefaultInstance('Note Type', 'NoteType', false, true, true);
            _filtersConfig.value = 'all';
            this.tileFilterConfig.config.push(_filtersConfig);
        };
        const initEntityType = (item) => {
            if (item.field === 'entity__type_id') {
                _filtersConfig = this._tileFilterService.getNewFilterDefaultInstance('Entity Type', 'EntityType', false, true, true);
                if (item && item.value) {
                    const value = item.value.slice(1, -1).split(',');
                    _filtersConfig.value = value.map(data => data.slice(1, -1));
                } else {
                    _filtersConfig.value = 'all';
                }
                this.tileFilterConfig.config.push(_filtersConfig);
            }
        };
        const initNodeType = (item) => {
            if (item.field === 'entry__type_id') {
                _filtersConfig = this._tileFilterService.getNewFilterDefaultInstance('Note Type', 'NoteType', false, true, true);
                if (item && item.value) {
                    const value = item.value.slice(1, -1).split(',');
                    _filtersConfig.value = value.map(data => data.slice(1, -1));
                } else {
                    _filtersConfig.value = 'all';
                }
                this.tileFilterConfig.config.push(_filtersConfig);
            }
        };
        if (this.tileConfig.filters && this.tileConfig.filters.filters[0] && this.tileConfig.filters.filters[0].filters) {
            const len = this.tileConfig.filters.filters[0].filters.length;
            if (len === 0) {
                initEntityAllValue();
                initNodeAllValue();
            } else if (len === 2) {
                this.tileConfig.filters.filters[0].filters.forEach(item => {
                    initEntityType(item);
                    initNodeType(item);
                });
            } else if (len === 1) {
                const item = this.tileConfig.filters.filters[0].filters[0];
                if (item.field === 'entity__type_id') {
                    initEntityType(item);
                    initNodeAllValue();
                } else {
                    initEntityAllValue();
                    initNodeType(item);
                }
            } else {
                this.tileConfig.filters.filters[0].filters.forEach(item => {
                    const filterConfig = this._tileFilterService.convertFilterTileToCommon(item);
                    if (filterConfig) {
                        this.tileFilterConfig.config.push(filterConfig);
                    }
                });
            }
        } else {
            initEntityAllValue();
            initNodeAllValue();
        }
        if (this.tileConfig.filters &&
            this.tileConfig.filters.filters[1] &&
            this.tileConfig.filters.filters[1].filters &&
            this.tileConfig.filters.filters[1].filters.length > 0) {
            this.tileConfig.filters.filters[1].filters.forEach(item => {
                const filterConfig = this._tileFilterService.convertFilterTileToCommon(item);
                if (filterConfig) {
                    this.tileFilterConfig.config.push(filterConfig);
                }
            });
        }
        // send mapFilter to tiltFilterConfig
        if (this.tileConfig.mapFilters) {
            this.tileFilterConfig.mapConfig = this.tileConfig.mapFilters;
        }
    }

    initTileSettingsData() {
        this.tileSettings.screenFocusType = this._screenFocusType;
        this.tileSettings.focus = this.tileConfig.focus;
        this.tileSettings.name = this.tileConfig.tileName;
        this.tileSettings.description = this.tileConfig.tileDescription;
        this.tileSettings.hideFocusSetting = this.tileConfig.visualization.type === TamDashboardTileType.EDITOR;
        this.tileSettings.type = this.tileConfig.visualization.type;
        if (this._tileOperate === TileOperations.COPY &&
            this.wizardConfig.config.active === this.wizardConfig.config.menuData.length - 1 && this.tileSettingsComp) {
            this.tileSettingsComp.refreshName();
        }
    }

    goBackAddTile() {
        const clearTile = () => {
            this.currentTileId = null;
            this.config.feedbackSubject$.next({ type: TileSettingEvents.clearActiveTile });
        };
        if (this.getModifyValue()) {
            const subscription = this._alertWindow.warn('You have unsaved changes',
                ['Are you sure you want to discard the changes?', 'Please click "Apply & Preview" to apply them']).subscribe((result: boolean) => {
                    if (result) {
                        this.delNewTile();
                        clearTile();
                    }
                    subscription.unsubscribe();
                });
        } else {
            clearTile();
        }
    }

    validChoose(): boolean {
        return this.tileChooseConfig.config.type !== null;
    }

    validFilter(): boolean {
        return this.tileFilterConfig.config.length >= 2 &&
            this.tileFilterConfig.config[0].fields[0] === 'entity__type_id' &&
            (this.tileFilterConfig.config[0].value === 'all' || this.tileFilterConfig.config[0].value.length > 0) &&
            this.tileFilterConfig.config[1].fields[0] === 'entry__type_id' &&
            (this.tileFilterConfig.config[1].value === 'all' || this.tileFilterConfig.config[1].value.length > 0);
    }

    validConfigureDataSource(): boolean {
        const item = this.wizardConfig.config.menuData[WIZARD_MENU.CONFIGURE];
        if (item.status === Status.notSet) {
            return true;
        }
        if (this.tileColumnSettingsComp) {
            return this.tileColumnSettingsComp.validate();
        }
        return this.wizardConfig.config.menuData[WIZARD_MENU.CONFIGURE].validStatus === ValidStatus.validPass;
    }

    validVisualization(): boolean {
        const item = this.wizardConfig.config.menuData[WIZARD_MENU.VISUALIZATION];
        if (item.status === Status.notSet) {
            return true;
        }
        if (this.tileChartComp) {
            return this.tileChartComp.validate();
        }
        return item.validStatus === ValidStatus.validPass;
    }

    validTileSetting(): boolean {
        if (this.tileSettingsComp) {
            const valid = this.tileSettingsComp.validSetting();
            if (valid) {
                const tileSettingInfo = this.tileSettingsComp.onSave();
                this.tileSettings.name = tileSettingInfo.name;
                this.tileSettings.description = tileSettingInfo.description;
                this.tileSettings.focus = tileSettingInfo.focus;
            }
            return valid;
        }
        return this.wizardConfig.config.menuData[WIZARD_MENU.TILE_SETTING].validStatus === ValidStatus.validPass;
    }

    validForm(index: number): boolean {
        if (index === WIZARD_MENU.CHOOSE) {
            return this.validChoose();
        }
        if (index === WIZARD_MENU.FILTER) {
            return this.validFilter();
        }
        if (index === WIZARD_MENU.CONFIGURE) {
            return this.validConfigureDataSource();
        }
        if (index === WIZARD_MENU.VISUALIZATION) {
            return this.validVisualization();
        }
        if (index === WIZARD_MENU.TILE_SETTING) {
            return this.validTileSetting();
        }
        return false;
    }

    subscriptions() {
        this._destroySubscriptions.push(
            this.wizardConfig.events$.pipe(
                filter((item) => item.type === WizardMenuEventType.changeSelect)
            ).subscribe((item) => {
                if (this.wizardConfig.config.active > item.payload) {
                    this.previous(item.payload);
                } else if (this.wizardConfig.config.active < item.payload) {
                    this.next(item.payload);
                }
            }),
            this.tileChooseConfig.events$.pipe(
                filter((item) => item.type === TileChooseEventType.tileTypeChange)
            ).subscribe((data) => {
                if (this.tileConfig.visualization.type === TamDashboardTileType.NOTES && data.payload !== TamDashboardTileType.NOTES) {
                    this.tileConfig = NoteTileSettingsService.cleanupNoteTileOnlySettings(this.tileConfig);
                }
                this.tileChooseConfig.config.type = data.payload;
                this.tileSettings.type = data.payload;
                if (data.payload === TamDashboardTileType.MAP && this.tileSettings.focus.type === ScreenFocusType.ALL) {
                    this.tileSettings.focus.type = ScreenFocusType.ENABLE_FOCUS_CONTROL;
                }
                const filterTab = this.wizardConfig.config.menuData[WIZARD_MENU.FILTER];
                const configure = this.wizardConfig.config.menuData[WIZARD_MENU.CONFIGURE];
                const visualization = this.wizardConfig.config.menuData[WIZARD_MENU.VISUALIZATION];
                const setVisualization = () => {
                    visualization.status = this._defaultVisualizationSettingsStatus;
                    const valid = this._validateVisualizationSettings(data.payload, this.tileConfig.visualization.settings || {});
                    visualization.validStatus = valid ? ValidStatus.validPass : ValidStatus.default;
                };
                const setConfigure = () => {
                    configure.status = Status.default;
                    this._defaultVisualizationSettingsStatus = Status.default;
                    const valid = this.validConfigureDataSource();
                    configure.validStatus = valid ? ValidStatus.validPass : ValidStatus.default;
                };
                if (data.payload === TamDashboardTileType.METADATA) {
                    filterTab.status = Status.default;
                    visualization.status = Status.notSet;
                    visualization.validStatus = ValidStatus.default;
                    setConfigure();
                } else if (data.payload === TamDashboardTileType.NOTES) {
                    filterTab.status = Status.default;
                    configure.status = Status.notSet;
                    configure.validStatus = ValidStatus.default;
                    setVisualization();
                } else if (data.payload === TamDashboardTileType.EDITOR
                    // || data.payload === TamDashboardTileType.MAP
                ) {
                    filterTab.status = Status.disabled;
                    configure.status = Status.disabled;
                    visualization.status = Status.disabled;
                    this.wizardConfig.config.menuData[WIZARD_MENU.TILE_SETTING].status = Status.default;
                } else {
                    filterTab.status = Status.default;
                    // note: call function setConfigure and setVisualization order
                    setConfigure();
                    setVisualization();
                }
                this.tileConfig.visualization.type = data.payload;
                this.tileSettings.hideFocusSetting = this.tileConfig.visualization.type === TamDashboardTileType.EDITOR;
                this.initFooterBtn();
            }),
            this.tileChooseConfig.events$.pipe(
                filter((item) => item.type === TileChooseEventType.tileDblClick)
            ).subscribe(() => {
                if (this.tileChooseConfig.config.type !== TamDashboardTileType.EDITOR) {
                    this.next(WIZARD_MENU.FILTER);
                } else {
                    this.next(WIZARD_MENU.TILE_SETTING);
                }
            }),
            this.tileFilterConfig.events$.pipe(
                filter((item) => item.type === TileFilterEventType.change)
            ).subscribe((data) => {
                this.tileFilterConfig.config = _.cloneDeep(data.payload);
            }),
            this.tileFilterConfig.events$.pipe(
                filter((item) => item.type === TileFilterEventType.mapChange)
            ).subscribe((data) => {
                this.tileFilterConfig.mapConfig = _.cloneDeep(data.payload);
            }),
            // receive config value
            this.config.actionSubject$.subscribe(action => this._onSubscribeActions(action)),
            this.addVisualizationsConfig.feedbackSubject$.subscribe(item => {
                if (item.type === DataEventType.clickIcon) {
                    this.addVisualizationType = item.payload;
                    this.config.feedbackSubject$.next({
                        type: TileSettingEvents.addTile,
                        payload: item.payload
                    });
                }
            }),
            this.config.actionSubject$.pipe(
                filter(action => action.type === TamTileSettingsEvents.RETURN_LATEST_SETTINGS)).subscribe((action: any) => {
                    if (this.tileConfig.visualization && action.payload.visualization) {
                        this.tileConfig.visualization.settings.hideHeader = action.payload.visualization.settings.hideHeader;
                        this.tileConfig.visualization.settings.hideBorder = action.payload.visualization.settings.hideBorder;
                        if (this.tileConfig.visualization.type === TamDashboardTileType.GRID) {
                            this.tileConfig.visualization.settings.gridSetting = action.payload.visualization.settings.gridSetting;
                        }
                    }
                    if (this.tileConfig.visualization.type === TamDashboardTileType.EDITOR) {
                        this.tileConfig.visualization.settings.html = action.payload.visualization.settings.html;
                    }
                    this._defaultTileConfig = _.cloneDeep(this.tileConfig);
                    this.config.feedbackSubject$.next({
                        type: TileSettingEvents.preview,
                        id: this.currentTileId,
                        payload: this.tileConfig,
                        refreshTile: action.refreshTile
                    });
                }),
            this.updateGridSettings$.subscribe((action) => {
                if (action['type'] === TamTileSettingsEvents.UPDATE_GRID_SETTINGS) {
                    this.tileConfig.visualization.settings.gridSetting = action['payload'];
                }
            }),
            this.tileColumnsChange$.subscribe((deleteItem: TileColumn) => {
                if (deleteItem.guid === this.tileConfig.visualization.settings.categoryAxis) {
                    const item = this.wizardConfig.config.menuData[WIZARD_MENU.VISUALIZATION];
                    item.validStatus = ValidStatus.validFailed;
                }
            }),
        );
    }

    showPreviewBtn() {
        let count = 0;
        this.wizardConfig.config.menuData.forEach(item => {
            if (item.validStatus === ValidStatus.validPass || item.status === Status.notSet) {
                count += 1;
            }
        });
        return !this.showNextBtn || count === this.wizardConfig.config.menuData.length;
    }

    calculateNextIndex() {
        let index = this.wizardConfig.config.active + 1;
        while (
            this.wizardConfig.config.menuData[index] && (
                this.wizardConfig.config.menuData[index].status === Status.notSet || (
                    (this.tileConfig.visualization.type === TamDashboardTileType.EDITOR
                        || this.tileConfig.visualization.type === TamDashboardTileType.MAP
                    ) &&
                    this.wizardConfig.config.menuData[index].status === Status.disabled))) {
            index += 1;
        }
        return index;
    }

    calculatePreviousIndex() {
        let index = this.wizardConfig.config.active - 1;
        while (
            this.wizardConfig.config.menuData[index].status === Status.notSet || (
                (this.tileConfig.visualization.type === TamDashboardTileType.EDITOR
                    || this.tileConfig.visualization.type === TamDashboardTileType.MAP
                ) &&
                this.wizardConfig.config.menuData[index].status === Status.disabled)) {
            index -= 1;
        }
        return index;
    }

    next(activeIndex: number) {
        const valid = this.validForm(this.wizardConfig.config.active);
        const activeMenu = this.wizardConfig.config.menuData[this.wizardConfig.config.active];
        if (!valid) {
            activeMenu.validStatus = ValidStatus.validFailed;
            let tempIndex = activeIndex;
            if (!tempIndex) {
                tempIndex = this.calculateNextIndex();
            }
            if (this.wizardConfig.config.menuData[tempIndex].validStatus !== ValidStatus.validFailed &&
                this.wizardConfig.config.menuData[tempIndex].validStatus !== ValidStatus.validPass) {
                return;
            }
        } else {
            activeMenu.validStatus = ValidStatus.validPass;
        }
        if (this.wizardConfig.config.active === WIZARD_MENU.TILE_SETTING) {
            return;
        }
        this.showPreviousBtn = true;
        if (activeIndex) {
            this.wizardConfig.config.active = activeIndex;
        } else {
            const curIndex = this.calculateNextIndex();
            if (curIndex === WIZARD_MENU.TILE_SETTING + 1) {
                this.showNextBtn = false;
                return;
            }
            this.wizardConfig.config.active = curIndex;
        }
        if (this.wizardConfig.config.menuData[this.wizardConfig.config.active]) {
            this.wizardConfig.config.menuData[this.wizardConfig.config.active].status = Status.selected;
        }
        const nextIndex = this.calculateNextIndex();
        if (nextIndex === WIZARD_MENU.TILE_SETTING + 1) {
            this.showNextBtn = false;
            return;
        }
        if (this.wizardConfig.config.menuData[nextIndex]) {
            this.wizardConfig.config.menuData[nextIndex].status = Status.selected;
        }
        this.showNextBtn = this.wizardConfig.config.active !== WIZARD_MENU.TILE_SETTING;
        this._defaultVisualizationSettingsStatus = this.wizardConfig.config.menuData[WIZARD_MENU.CONFIGURE].status;
    }

    previous(activeIndex?: number) {
        const activeMenu = this.wizardConfig.config.menuData[this.wizardConfig.config.active];
        if (!this.validForm(this.wizardConfig.config.active)) {
            activeMenu.validStatus = ValidStatus.validFailed;
            let tempIndex = activeIndex;
            if (!tempIndex) {
                tempIndex = this.calculatePreviousIndex();
            }
            if (this.wizardConfig.config.menuData[tempIndex].validStatus !== ValidStatus.validFailed &&
                this.wizardConfig.config.menuData[tempIndex].validStatus !== ValidStatus.validPass) {
                return;
            }
        } else {
            activeMenu.validStatus = ValidStatus.validPass;
        }
        if (this.wizardConfig.config.active === WIZARD_MENU.CHOOSE) {
            return;
        }
        this.showNextBtn = true;
        if (activeIndex || activeIndex === WIZARD_MENU.CHOOSE) {
            this.wizardConfig.config.active = activeIndex;
        } else {
            this.wizardConfig.config.active = this.calculatePreviousIndex();
        }

        this.showPreviousBtn = this.wizardConfig.config.active !== WIZARD_MENU.CHOOSE;
        this.wizardConfig.config.menuData[this.wizardConfig.config.active].status = Status.selected;
        this._defaultVisualizationSettingsStatus = this.wizardConfig.config.menuData[WIZARD_MENU.CONFIGURE].status;
    }

    formatTileData() {
        return this.tileConfig.visualization;
    }

    formatFilterData() {
        const filters = [];

        const disabledFilter = this.tileFilterConfig.config.filter(item => item.disable);
        filters[0] = new TileFilter();
        filters[0].filters = this._tileFilterService.getFilters(disabledFilter);
        filters[0].filters.forEach(item => {
            delete item.dataType;
            delete item.name;
            delete item.list;
        });
        const enalbeFilter = this.tileFilterConfig.config.filter(item => !item.disable);
        if (enalbeFilter && enalbeFilter.length > 0) {
            filters[1] = new TileFilter();
            filters[1].filters = this._tileFilterService.getFilters(enalbeFilter);
        }
        return filters;
    }

    preview(clean = false) {
        if (this.validateSettings()) {
            this._tileOperate = '';
            this.tileConfig = this._getNewTileConfig();
            if (clean) {
                this._cleanupForcedDirty();
            }
            this.config.feedbackSubject$.next({
                type: TileSettingEvents.pullTileSettings,
                id: this.currentTileId,
                payload: this.tileConfig
            });
        }
    }

    setModifyValue(value: string) {
        if (value === '') {
            this.tileConfig.visualization.dirty = false;
            this.tileConfig.visualization.settings.html = '';
        } else if (this.tileConfig.visualization.settings.html !== value) {
            this.tileConfig.visualization.dirty = true;
            this.tileConfig.visualization.settings.html = value;
        }
    }

    // Tips: Be aware of this function's behavior, if set entity type filter to empty, we may assume return value is true
    // But actually it returns false
    getModifyValue() {
        const config = this._getNewTileConfig();
        /** filter level is too deep, lodash isEqual return false */
        const newStr = JSON.stringify(config);
        const oldStr = JSON.stringify(this._defaultTileConfig);
        const hadModifyFilter: boolean = !_.isEqual(JSON.parse(oldStr), JSON.parse(newStr));
        // comments the below code, since the hadModifyFilter can judge the below behavior
        // if (this.tileConfig.visualization.dirty === true) {
        //     return true;
        // } else {
        //     this._cleanupForcedDirty();
        // }
        return this.currentTileId && (this._tileOperate === TileOperations.ADD || hadModifyFilter);
    }

    delNewTile() {
        if (!this.tileConfig.tileName) {
            this._cleanupForcedDirty();
            this.config.feedbackSubject$.next({
                type: TileSettingEvents.deleteTile,
                payload: this.currentTileId
            });
        }
    }

    validateSettings(): boolean {
        if (this.currentTileId) {
            let validFailCount = 0;
            this.wizardConfig.config.menuData.forEach((item, i) => {
                if (item['status'] !== Status.disabled) {
                    const valid = this.validForm(i);
                    if (!valid) {
                        validFailCount += 1;
                    }
                    if (item.status !== Status.notSet) {
                        item.validStatus = valid ? ValidStatus.validPass : ValidStatus.validFailed;
                    }
                }
            });
            return validFailCount === 0;
        } else {
            return true;
        }
    }

    private _changeItemCLick(action) {
        // tile focus and click self, do nothing
        if (this.currentTileId && action.payload && action.payload.event.id === this.currentTileId) {
            return;
        }
        if (this.getModifyValue()) {
            const subscription = this._alertWindow.warn('You have unsaved changes',
                ['Are you sure you want to discard the changes?',
                    'Please click "Apply & Preview" to apply them']).subscribe((result: boolean) => {
                        if (result) {
                            this.delNewTile();
                            this.config.feedbackSubject$.next({
                                type: TileSettingEvents.changeTile,
                                payload: action.payload
                            });
                        }
                        subscription.unsubscribe();
                    });
        } else {
            this.config.feedbackSubject$.next({
                type: TileSettingEvents.changeTile,
                payload: action.payload
            });
        }
    }

    private _getNewTileConfig() {
        const config = _.cloneDeep(this.tileConfig);
        config.visualization = this.formatTileData();
        config.filters.filters = this.formatFilterData();
        if (this.tileFilterConfig.mapConfig) {
            // For non-map tiles, they do NOT need map filters.
            // Set mapFilters attribute as null might be led the wrong calculation of dirty setting.
            config.mapFilters = this.tileFilterConfig.mapConfig;
        }
        const newDefaultArr = [];
        config.filters.filters[0].filters.forEach(item => {
            if (item.value !== 'all') {
                newDefaultArr.push(item);
            }
        });
        config.filters.filters[0].filters = newDefaultArr;
        // when click Apply&Preview on other page, not tile settings page
        if (this.tileSettingsComp) {
            const tileSettingInfo = this.tileSettingsComp.onSave();
            config.tileName = tileSettingInfo.name;
            config.tileDescription = tileSettingInfo.description;
            config.focus = tileSettingInfo.focus;
        } else {
            config.tileName = this.tileSettings.name;
            config.tileDescription = this.tileSettings.description;
            config.focus = this.tileSettings.focus;
        }
        // delete version 1.0 useless params
        const settings = config.visualization.settings;
        if (config.visualization.type === this.visualizationType.PIE_CHART) {
            delete settings.categorySort;
            delete settings.categoryCount;
            delete settings.groupBy;
            delete settings.column;
            delete settings.summaries;
        } else if (config.visualization.type === this.visualizationType.COLUMN_CHART ||
            config.visualization.type === this.visualizationType.BAR_CHART) {
            delete settings.groupBy;
            delete settings.column;
            delete settings.summaries;
        } else if (config.visualization.type === this.visualizationType.LINE_CHART) {
            delete settings.categorySort;
            delete settings.categoryCount;
            delete settings.column;
            delete settings.summaries;
            delete settings.enableBlankCategory;
        } else if (config.visualization.type === this.visualizationType.VALUE) {
            delete settings.categorySort;
            delete settings.categoryCount;
            delete settings.groupBy;
            delete settings.categoryAxis;
            delete settings.series;
            delete settings.enableBlankCategory;
        }
        if (config.visualization.type !== this.visualizationType.GRID) {
            delete settings.gridSetting;
        }
        if (this.tileConfig.conditionalFormattings && this.tileConfig.conditionalFormattings.length > 0) {
            config.conditionalFormattings = this.tileConfig.conditionalFormattings;
        }
        return config;
    }

    private _loadSetting(action) {
        this.tileConfig = new TileModel();
        this.tileConfig.mapFilters = null;
        this.currentTileId = action.id;
        this._tileOperate = action.payload && action.payload.operateTile;
        if (action.payload && action.payload.tileSetting) {
            this.tileConfig = action.payload.tileSetting;
            this._screenFocusType = action.payload.screenFocusType;
        }
        if (this._tileOperate === TileOperations.ADD) {
            this.tileConfig.visualization.type = this.addVisualizationType;
            this.config.feedbackSubject$.next({
                type: TileSettingEvents.initTileSettings,
                id: this.currentTileId,
                payload: this.tileConfig
            });
            if (this.tileConfig.visualization.type === this.visualizationType.GRID) {
                this.tileConfig.visualization.settings.gridSetting = new GridVisualizationSettings();
            }
        } else {
            // old dashboard - create before add this feature
            if (this.tileConfig.visualization.type === this.visualizationType.GRID &&
                !this.tileConfig.visualization.settings.gridSetting) {
                this.tileConfig.visualization.settings.gridSetting = new GridVisualizationSettings();
            }
        }
        // old some tile relationshipTypes = [], some have not relationshipTypes parameter
        // new create tile if relationshipTypes = [], will not save relationshipTypes parameter,
        // compare old tile config and new tile config, should not show alert.
        if (this.tileConfig.focus && this.tileConfig.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL && this.tileConfig.focus.relationshipTypes &&
            this.tileConfig.focus.relationshipTypes.length === 0) {
            delete this.tileConfig.focus.relationshipTypes;
        }
        this._defaultTileConfig = _.cloneDeep(this.tileConfig);
        this.initWizardData();
        this.initTileConfigData();
    }

    private _onSubscribeActions(action) {
        if (action.type === TamTileSettingsEvents.LOAD_SETTING) {
            this._loadSetting(action);
        } else if (action.type === TamTileSettingsEvents.CHANGE_SCREEN_FOCUS_TYPE) {
            this._screenFocusType = action.payload;
            this.tileSettings.screenFocusType = this._screenFocusType;
            if (this.tileSettings.screenFocusType !== ScreenFocusType.ENABLE_FOCUS_CONTROL &&
                this.tileSettings.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL &&
                this.tileSettings.focus.relationshipTypes) {
                const len = this.tileSettings.focus.relationshipTypes.length;
                if (len > 0) {
                    delete this.tileSettings.focus.relationshipTypes;
                    // when the tile blur focus, avoid alert
                    delete this._defaultTileConfig.focus.relationshipTypes;
                }
            }
            if (this.tileSettingsComp) {
                this.tileSettingsComp.refreshFocus();
            }
        } else if (action.type === TamTileSettingsEvents.CHANGE_TILE_DEFAULT_LAYOUT) {
            this._defaultTileConfig['layout'] = action.payload;
            this.tileConfig['layout'] = action.payload;
        } else if (action.type === TamTileSettingsEvents.CHANGE_ITEM_CLICK) {
            this._changeItemCLick(action);
        } else if (action.type === TamDashboardEvents.ON_COLUMN_ORDER_CHANGED) {
            this.tileConfig.columns = TileSettingService.sortColumnsByIdList(this.tileConfig.columns, action.payload.columnIds);
            this._defaultTileConfig.columns = TileSettingService.sortColumnsByIdList(this._defaultTileConfig.columns, action.payload.columnIds);
        } else if (action.type === TamDashboardEvents.RESIZE_COLUMN_WIDTH) {
            // must be a entire tile
            if (this.tileConfig.tileName && this.tileConfig.visualization.settings.gridSetting) {
                this.tileConfig.visualization.settings.gridSetting.automaticSizing = false;
                this._defaultTileConfig.visualization.settings.gridSetting.automaticSizing = false;
                // if current tile setting page is visualization
                if (this.tileChartComp) {
                    this.tileChartComp.refresh();
                }
            }
        }
    }

    private _validateVisualizationSettings(type, settings) {
        if (type === this.visualizationType.PIE_CHART) {
            if (!settings.categoryAxis || !settings.series || !settings.displaySetting) {
                return false;
            }
        } else if (type === this.visualizationType.COLUMN_CHART ||
            type === this.visualizationType.BAR_CHART) {
            if (!this._barChartLogicValidate(settings)) {
                return false;
            }
            if (!settings.categoryAxis || !settings.series) {
                return false;
            }
        } else if (type === this.visualizationType.LINE_CHART) {
            if (!settings.categoryAxis || !settings.series || !settings.groupBy) {
                return false;
            }
        } else if (type === this.visualizationType.VALUE) {
            if (!settings.column) {
                return false;
            }
        }
        return true;
    }

    private _barChartLogicValidate(settings) {
        if (!settings.categoryCount && !settings.categorySort) {
            return true;
        }
        if (settings.categoryCount && !settings.categorySort) {
            return false;
        }
        if (settings.categorySort && !settings.categoryCount) {
            return false;
        }
        if (settings.categoryCount > 10 || settings.categoryCount <= 0) {
            return false;
        }
        return true;
    }

    private _prepareNoteTileDefaults() {
        this.wizardConfig.config.menuData[WIZARD_MENU.CONFIGURE].status = Status.notSet;
        // this.tileConfig.options.showRowAsThread = true;
        if (this.tileConfig.columns.filter(column => column.field === 'entry__source_id').length === 0) {
            this.tileConfig.columns.push(defaultSource);
        }
        if (this.tileConfig.columns.filter(column => column.field === 'entry__submitter_id').length === 0) {
            this.tileConfig.columns.push(defaultSubmitter);
        }
        if (this.tileConfig.columns.filter(column => column.field === 'entry__class').length === 0) {
            const column = Object.assign({}, defaultEntryClass);
            column.guid = TileSettingService.generateShortId(this.tileConfig.columns);
            this.tileConfig.columns.push(column);
        }
    }

    private _cleanupForcedDirty() {
        this.tileConfig.visualization.dirty = undefined;
        this._defaultTileConfig.visualization.dirty = undefined;
        delete this.tileConfig.visualization.dirty;
        delete this._defaultTileConfig.visualization.dirty;
    }
}
